import { Button, Checkbox, Select, Tooltip } from "antd";
import { useMemo, type ReactNode } from "react";
import { compareStringsAlphabetically } from "utils/utils";
import "./schedule-hierarchy.scss";

export const SELECT_ALL_VALUE = "__select_all__";

export type FilterState = {
  phase: string[];
  csiDivision: string[];
  activityType: string[];
  location: string[];
};

export const EMPTY_FILTERS: FilterState = {
  phase: [],
  csiDivision: [],
  activityType: [],
  location: []
};

export type FilterRow = {
  phase?: string;
  csi_division?: string;
  activity_type?: string;
  location_tags?: string;
};

export function matchesScheduleFilters(
  row: FilterRow,
  filters: FilterState
): boolean {
  if (filters.phase.length > 0 && !filters.phase.includes(row.phase || "")) {
    return false;
  }
  if (
    filters.csiDivision.length > 0 &&
    !filters.csiDivision.includes(row.csi_division || "")
  ) {
    return false;
  }
  if (
    filters.activityType.length > 0 &&
    !filters.activityType.includes(row.activity_type || "")
  ) {
    return false;
  }
  if (
    filters.location.length > 0 &&
    !filters.location.includes(row.location_tags || "")
  ) {
    return false;
  }
  return true;
}

export function nextMultiSelectValues(
  next: string[] | undefined,
  visibleOptions: string[],
  currentSelected: string[]
): string[] {
  const selectedNext = next ?? [];
  const withoutSelectAll = selectedNext.filter(
    (value) => value !== SELECT_ALL_VALUE
  );
  if (!selectedNext.includes(SELECT_ALL_VALUE)) {
    return withoutSelectAll;
  }
  const allVisibleSelected =
    visibleOptions.length > 0 &&
    visibleOptions.every((value) => currentSelected.includes(value));
  return allVisibleSelected ? [] : [...visibleOptions];
}

export type FilterOptions = {
  phases: string[];
  activityTypes: string[];
  csiDivisions: string[];
  locations: string[];
};

export function buildFilterOptionsFromRows(
  rows: Array<{
    phase?: string;
    csi_division?: string;
    activity_type?: string;
    location_tags?: string;
  }>
): FilterOptions {
  const phases = new Set<string>();
  const activityTypes = new Set<string>();
  const csiDivisions = new Set<string>();
  const locations = new Set<string>();

  for (const row of rows) {
    if (row.phase && row.phase !== "Root") phases.add(row.phase);
    if (row.activity_type && row.activity_type !== "Summary") {
      activityTypes.add(row.activity_type);
    }
    if (row.csi_division) csiDivisions.add(row.csi_division);
    if (row.location_tags) locations.add(row.location_tags);
  }

  return {
    phases: [...phases].sort(compareStringsAlphabetically),
    activityTypes: [...activityTypes].sort(compareStringsAlphabetically),
    csiDivisions: [...csiDivisions].sort(compareStringsAlphabetically),
    locations: [...locations].sort(compareStringsAlphabetically)
  };
}

export function selectedOptionsTooltip(values: string[]) {
  return values.length > 1 ? values.join(", ") : undefined;
}

function OverflowSelectTooltip({
  selected,
  children
}: {
  selected: string[];
  children: ReactNode;
}) {
  return (
    <Tooltip title={selectedOptionsTooltip(selected)}>
      <span className="inline-block">{children}</span>
    </Tooltip>
  );
}

function toSelectOptions(visibleOptions: string[]) {
  const options = visibleOptions.map((value) => ({
    label: value,
    value,
    title: value
  }));
  if (visibleOptions.length === 0) return options;
  return [
    { label: "Select All", value: SELECT_ALL_VALUE, title: "Select All" },
    ...options
  ];
}

type ScheduleFilterBarProps = {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  showTags?: boolean;
  onShowTagsChange?: (show: boolean) => void;
  totalCount: number;
  filteredCount: number;
  filterOptions: FilterOptions;
  /** All data rows (non-summary) for cascading filter logic */
  allRows: Array<{
    phase?: string;
    csi_division?: string;
    activity_type?: string;
    location_tags?: string;
    activity_name?: string;
    activity_id?: string;
  }>;
  /** Search text to narrow cascading filter options */
  searchText?: string;
  /** Hide the count display in the bottom bar */
  hideCount?: boolean;
};

export default function ScheduleFilterBar({
  filters,
  onFilterChange,
  onExpandAll,
  onCollapseAll,
  showTags,
  onShowTagsChange,
  totalCount,
  filteredCount,
  filterOptions,
  allRows,
  searchText,
  hideCount
}: ScheduleFilterBarProps) {
  const hasFilters =
    filters.phase.length > 0 ||
    filters.csiDivision.length > 0 ||
    filters.activityType.length > 0 ||
    filters.location.length > 0;

  // Cascading: compute available options per category (respects search text)
  const { availPhases, availTypes, availCsi, availLocs } = useMemo(() => {
    const lowerSearch = (searchText || "").trim().toLowerCase();

    // Pre-filter rows by search text
    const filteredRows = lowerSearch
      ? allRows.filter((row) => {
          const nameMatch = (row.activity_name || "")
            .toLowerCase()
            .includes(lowerSearch);
          const idMatch = (row.activity_id || "")
            .toLowerCase()
            .includes(lowerSearch);
          return nameMatch || idMatch;
        })
      : allRows;

    const phaseSet = new Set(filters.phase);
    const csiSet = new Set(filters.csiDivision);
    const typeSet = new Set(filters.activityType);
    const locSet = new Set(filters.location);

    const aP = new Set<string>();
    const aT = new Set<string>();
    const aC = new Set<string>();
    const aL = new Set<string>();

    for (const row of filteredRows) {
      const matchPhase = phaseSet.size === 0 || phaseSet.has(row.phase || "");
      const matchCsi = csiSet.size === 0 || csiSet.has(row.csi_division || "");
      const matchType =
        typeSet.size === 0 || typeSet.has(row.activity_type || "");
      const matchLoc = locSet.size === 0 || locSet.has(row.location_tags || "");

      if (matchCsi && matchType && matchLoc && row.phase) aP.add(row.phase);
      if (matchPhase && matchCsi && matchLoc && row.activity_type)
        aT.add(row.activity_type);
      if (matchPhase && matchType && matchLoc && row.csi_division)
        aC.add(row.csi_division);
      if (matchPhase && matchCsi && matchType && row.location_tags)
        aL.add(row.location_tags);
    }

    return { availPhases: aP, availTypes: aT, availCsi: aC, availLocs: aL };
  }, [allRows, filters, searchText]);

  const phaseSelected = new Set(filters.phase);
  const typeSelected = new Set(filters.activityType);
  const csiSelected = new Set(filters.csiDivision);
  const locSelected = new Set(filters.location);

  const rowsHavePhase = allRows.some((row) => Boolean(row.phase));
  const rowsHaveType = allRows.some((row) => Boolean(row.activity_type));
  const rowsHaveCsi = allRows.some((row) => Boolean(row.csi_division));
  const rowsHaveLoc = allRows.some((row) => Boolean(row.location_tags));

  const visiblePhases = rowsHavePhase
    ? filterOptions.phases.filter(
        (v) => availPhases.has(v) || phaseSelected.has(v)
      )
    : filterOptions.phases;
  const visibleTypes = rowsHaveType
    ? filterOptions.activityTypes.filter(
        (v) => availTypes.has(v) || typeSelected.has(v)
      )
    : filterOptions.activityTypes;
  const visibleCsi = rowsHaveCsi
    ? filterOptions.csiDivisions.filter(
        (v) => availCsi.has(v) || csiSelected.has(v)
      )
    : filterOptions.csiDivisions;
  const visibleLocs = rowsHaveLoc
    ? filterOptions.locations.filter(
        (v) => availLocs.has(v) || locSelected.has(v)
      )
    : filterOptions.locations;

  const commonSelectProps = {
    showSearch: true,
    allowClear: true,
    removeIcon: null,
    maxTagCount: "responsive" as const,
    filterOption: (input: string, option: any) => {
      if (option?.value === SELECT_ALL_VALUE) return true;
      return (option?.value ?? "").toLowerCase().includes(input.toLowerCase());
    }
  };

  return (
    <div className="px-6 py-1.5">
      {/* Filter dropdowns with uppercase labels */}
      <div className="flex items-end gap-4 flex-wrap">
        <div className="flex flex-col">
          <span className="sch-filter-label">PHASE</span>
          <OverflowSelectTooltip selected={filters.phase}>
            <Select
              {...commonSelectProps}
              mode="multiple"
              placeholder="Select Phase"
              value={filters.phase}
              onChange={(val: string[]) =>
                onFilterChange({
                  ...filters,
                  phase: nextMultiSelectValues(
                    val,
                    visiblePhases,
                    filters.phase
                  )
                })
              }
              style={{ minWidth: 140 }}
              notFoundContent={
                <span className="text-xs text-gray-400">
                  No applicable options
                </span>
              }
              options={toSelectOptions(visiblePhases)}
            />
          </OverflowSelectTooltip>
        </div>

        <div className="flex flex-col">
          <span className="sch-filter-label">ACTIVITY TYPE</span>
          <OverflowSelectTooltip selected={filters.activityType}>
            <Select
              {...commonSelectProps}
              mode="multiple"
              placeholder="Select Activity Type"
              value={filters.activityType}
              onChange={(val: string[]) =>
                onFilterChange({
                  ...filters,
                  activityType: nextMultiSelectValues(
                    val,
                    visibleTypes,
                    filters.activityType
                  )
                })
              }
              style={{ minWidth: 180 }}
              notFoundContent={
                <span className="text-xs text-gray-400">
                  No applicable options
                </span>
              }
              options={toSelectOptions(visibleTypes)}
            />
          </OverflowSelectTooltip>
        </div>

        <div className="flex flex-col">
          <span className="sch-filter-label">CSI DIVISION</span>
          <OverflowSelectTooltip selected={filters.csiDivision}>
            <Select
              {...commonSelectProps}
              mode="multiple"
              placeholder="Select Division"
              value={filters.csiDivision}
              onChange={(val: string[]) =>
                onFilterChange({
                  ...filters,
                  csiDivision: nextMultiSelectValues(
                    val,
                    visibleCsi,
                    filters.csiDivision
                  )
                })
              }
              style={{ minWidth: 170 }}
              notFoundContent={
                <span className="text-xs text-gray-400">
                  No applicable options
                </span>
              }
              options={toSelectOptions(visibleCsi)}
            />
          </OverflowSelectTooltip>
        </div>

        <div className="flex flex-col">
          <span className="sch-filter-label">LOCATION</span>
          <OverflowSelectTooltip selected={filters.location}>
            <Select
              {...commonSelectProps}
              mode="multiple"
              placeholder="Select Location"
              value={filters.location}
              onChange={(val: string[]) =>
                onFilterChange({
                  ...filters,
                  location: nextMultiSelectValues(
                    val,
                    visibleLocs,
                    filters.location
                  )
                })
              }
              style={{ minWidth: 150 }}
              notFoundContent={
                <span className="text-xs text-gray-400">
                  No applicable options
                </span>
              }
              options={toSelectOptions(visibleLocs)}
            />
          </OverflowSelectTooltip>
        </div>

        {hasFilters && (
          <div className="flex items-center self-end">
            <Button
              size="small"
              danger
              onClick={() => onFilterChange(EMPTY_FILTERS)}
              style={{ height: 32 }}
            >
              Clear All
            </Button>
          </div>
        )}
        <div className="flex items-center gap-2 ml-auto h-8">
          {onShowTagsChange && (
            <Checkbox
              checked={showTags}
              onChange={(e) => onShowTagsChange(e.target.checked)}
              className="text-xs"
            >
              Show Tags
            </Checkbox>
          )}
          {onExpandAll && (
            <Button size="small" onClick={onExpandAll}>
              Expand All
            </Button>
          )}
          {onCollapseAll && (
            <Button size="small" onClick={onCollapseAll}>
              Collapse All
            </Button>
          )}
        </div>
      </div>

      {/* Bottom bar: count on left, Expand/Collapse + Show Tags on right */}
      {!hideCount && (
        <div className="flex items-center">
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {hasFilters || (searchText && searchText.trim())
              ? `${filteredCount.toLocaleString()} of ${totalCount.toLocaleString()}`
              : `${totalCount.toLocaleString()}`}
          </span>
        </div>
      )}
    </div>
  );
}
